"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqItems = [{
  id: "item-1",
  question: "How does life insurance transfer wealth without probate delays?",
  answer: "Life insurance can provide a death benefit to the beneficiaries named in the policy when the insured person dies and the policy is in force.\n\nLife insurance proceeds received because of the insured person's death are generally excluded from federal gross income, although exceptions can apply and interest paid on proceeds may be taxable.\n\nWhen proceeds are payable directly to a named beneficiary rather than to the insured person's estate, they generally pass directly to that beneficiary. Estate, ownership, beneficiary, and tax circumstances can affect the result."
}, {
  id: "item-2",
  question: "Can annuities provide guaranteed income to a surviving spouse?",
  answer: "Some annuity contracts offer payout or income options that can continue payments for the lives of two people, such as spouses. These may include joint-and-survivor annuity options or certain lifetime-income features.\n\nThe amount and duration of income, access to remaining contract value, death benefits, and what happens after either person dies depend on the specific annuity contract and the option selected.\n\nGuarantees are subject to the terms of the contract and the claims-paying ability of the issuing insurance company."
}, {
  id: "item-3",
  question: "What is the difference between primary and contingent beneficiaries?",
  answer: "A primary beneficiary is the person or entity designated to receive policy or contract proceeds when the insured or contract owner dies, according to the terms of the policy or contract.\n\nA contingent beneficiary is generally next in line to receive the proceeds if the primary beneficiary cannot receive them under the contract's provisions.\n\nBeneficiary rules can vary by policy, contract, ownership arrangement, and applicable law. Keeping beneficiary designations current is an important part of maintaining insurance and annuity contracts."
}, {
  id: "item-4",
  question: "How do fixed indexed annuities protect assets passed to heirs?",
  answer: "A fixed indexed annuity is not directly invested in the market index. The index is used as part of the contract's method for determining interest credits.\n\nA fixed indexed annuity may provide a death benefit or remaining contract value to a designated beneficiary, depending on the terms of the contract.\n\nFixed indexed annuities generally protect contract value from direct losses caused solely by declines in the referenced market index. However, withdrawals, surrender charges, optional rider charges, contract adjustments, and other provisions may affect the contract value or death benefit.\n\nThe amount available to beneficiaries and how it is paid depend on the specific annuity contract."
}, {
  id: "item-5",
  question: "When should our family review legacy and beneficiary documents?",
  answer: "Beneficiary designations should be reviewed periodically and when important life events occur, such as marriage, divorce, birth or adoption, death of a beneficiary, or other significant family or financial changes.\n\nThere is no single review schedule that is appropriate for everyone. The important point is to keep beneficiary information current and consistent with your intentions.\n\nBeneficiary designations can have significant legal and tax consequences in some circumstances. An attorney or tax professional can provide guidance when legal or tax advice is needed."
}];
export default function Legacy() {
  const shouldReduceMotion = useReducedMotion();
  const motionVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="legacy-planning" data-nav="light" className="relative bg-background py-20 md:py-28">
      <div className="mx-auto max-w-[1240px] px-5 md:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={motionVariants} className="mx-auto max-w-2xl text-center">
          <div className="flex justify-center">
            <Badge variant="outline" className="rounded-full border-border bg-muted/60 px-3.5 py-1 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              Legacy Planning
            </Badge>
          </div>

          <h2 className="mt-4 font-default text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            Passing wealth to the people and causes you care about
          </h2>

          <div className="mx-auto mt-4 flex items-center justify-center gap-2">
            <span className="bg-primary size-1.5 rounded-full" />
            <span className="bg-border h-px w-10" />
            <span className="bg-primary size-1.5 rounded-full" />
            <span className="bg-border h-px w-10" />
            <span className="bg-primary size-1.5 rounded-full" />
            <span className="bg-border h-px w-10" />
            <span className="bg-primary size-1.5 rounded-full" />
          </div>

          <p className="mt-3.5 font-default text-base leading-relaxed text-muted-foreground sm:text-lg">
            Straightforward guidance on beneficiary designations, estate protection, and tax-efficient wealth transfer.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} variants={motionVariants} className="mx-auto mt-12 max-w-[840px]">
          <Accordion type="single" collapsible className="w-full space-y-3.5">
            {faqItems.map((item, i) => <div data-index={i} key={item.id}>
                <AccordionItem value={item.id} className="rounded-lg border border-border bg-card px-5 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/30 hover:shadow-sm sm:px-6">
                  <AccordionTrigger className="py-5 font-default text-left text-base font-semibold text-card-foreground hover:no-underline sm:text-lg">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 pt-1 font-default text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              </div>)}
          </Accordion>

          <div className="mt-10 rounded-xl border border-border bg-card p-6 text-center shadow-xs sm:p-8">
            <h3 className="font-default text-lg font-semibold text-card-foreground sm:text-xl">
              Have specific questions about your family plan?
            </h3>
            <p className="mx-auto mt-2 max-w-lg font-default text-sm leading-relaxed text-muted-foreground sm:text-base">
              Speak with a licensed advisor for clear, independent guidance with zero sales pressure.
            </p>
            <div className="mt-6 flex justify-center">
              <Button asChild variant="outline" size="lg" className="rounded-md px-6 font-medium">
                <Link to="/FAQKnowledgeCenterPage">Browse all topics</Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}